import React, {useEffect, useState} from 'react';
import {message} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import AddVendorRepsComponent from '../../../Components/UserComponents/AddVendorReps';
import {getAllChapters} from '../../../Store/Actions';
import {getEventOptionsByEventId} from '../../../Store/Actions/eventOptions';
import {
  getSponsorshipReps,
  payForSponsorReps,
  postAttendee,
  removePendingSponsorRep,
  UpdateAttendee,
} from '../../../Store/Actions/eventRegister';
import {
  createSelectionOptionsUtil,
  createSelectionOptionsUtilForPerks,
} from '../../../Util/commonUtility';
import useLocalization from '../../../Localization';

const AddVendorRepsContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {ApplicationString} = useLocalization();
  const {eventId, sponsorRegistrationId} = useParams();
  const allChapters = useSelector((state) => state.chapters.allChapters);
  const {getEventOptionByEventId} = useSelector(
    ({eventOptions}) => eventOptions,
  );
  const [sponsorshipData, setSponsorshipData] = useState(null);

  const fetchSponsorship = async () => {
    const data = await dispatch(getSponsorshipReps(sponsorRegistrationId));
    return data || null;
  };

  useEffect(() => {
    let cancelled = false;
    // Drop the previous visit's rows before refetching. Back from checkout
    // remounts this page; a stale empty table must not render first.
    setSponsorshipData(null);
    fetchSponsorship().then((data) => {
      if (!cancelled && data) {
        setSponsorshipData(data);
      }
    });
    dispatch(getEventOptionsByEventId(eventId));
    if (!allChapters) {
      dispatch(getAllChapters());
    }
    return () => {
      cancelled = true;
    };
  }, [eventId, sponsorRegistrationId, location.key]);

  const eventLocalChapterData = allChapters?.data?.chapters?.find(
    (chapter) => chapter.id === sponsorshipData?.event?.local_chapter_id,
  );

  // Food options only when they belong to this event (the store slot is shared across pages).
  const foodOptions =
    getEventOptionByEventId?.data?.food?.filter(
      (option) => Number(option?.event_id) === Number(eventId),
    ) || [];
  const perk = sponsorshipData?.sponsorship_option?.eventSponsorshipPerk;
  const discountedFoodOptions = createSelectionOptionsUtilForPerks(
    foodOptions,
    perk?.free_lunch,
    perk?.lunch_price,
    'id',
    'name',
    eventLocalChapterData,
    'price',
    ApplicationString,
  );
  const regularFoodOptions = createSelectionOptionsUtil(
    foodOptions,
    'id',
    'name',
    'price',
    eventLocalChapterData,
  );
  const allEventOptionsFoodData = [
    ...(discountedFoodOptions || []),
    ...(regularFoodOptions || []),
    {
      value: 'no-food',
      label: ApplicationString['addAttendeesComponent.label.noFoodOption'],
    },
  ];

  const refreshAfterSave = async () => {
    const data = await fetchSponsorship();
    if (data) {
      setSponsorshipData(data);
    }
  };

  const saveRep = (
    data,
    setEditingKey,
    form,
    setIsAddAttendee,
    setDisableAttendeeClicks,
    setDisablePaymentClicks,
    setDisableEditClicks,
    setIsSavingAttendee,
  ) => {
    const usesPerk = Boolean(
      data?.attending_presentation ||
        data?.attending_dinner ||
        data?.food_option?.is_discounted,
    );
    if (sponsorshipData?.can_add_free_reps === false && !usesPerk) {
      message.error(ApplicationString['sponsorshipReps.limitReached']);
      setIsSavingAttendee && setIsSavingAttendee(false);
      setDisableAttendeeClicks && setDisableAttendeeClicks(false);
      setDisablePaymentClicks && setDisablePaymentClicks(false);
      setDisableEditClicks && setDisableEditClicks(false);
      return;
    }
    dispatch(
      postAttendee(
        {
          ...data,
          is_reps: true,
          parent_sponsor_id: Number(sponsorRegistrationId),
        },
        setEditingKey,
        form,
        setIsAddAttendee,
        setDisableAttendeeClicks,
        setDisablePaymentClicks,
        setDisableEditClicks,
        setIsSavingAttendee,
      ),
    ).then((saved) => {
      // A failed save (for example duplicate email) must keep the draft row so the
      // user can correct the email without re-entering every field.
      if (saved) {
        return refreshAfterSave();
      }
    });
  };

  const updateRep = (
    data,
    registrationId,
    setEditingKey,
    form,
    setDisableAttendeeClicks,
    setDisablePaymentClicks,
    setDisableEditClicks,
    setIsSavingAttendee,
  ) => {
    dispatch(
      UpdateAttendee(
        {
          ...data,
          is_reps: true,
          parent_sponsor_id: Number(sponsorRegistrationId),
          event_id: Number(eventId),
        },
        registrationId,
        setEditingKey,
        form,
        setDisableAttendeeClicks,
        setDisablePaymentClicks,
        setDisableEditClicks,
        setIsSavingAttendee,
      ),
    ).then((saved) => {
      if (saved) {
        return refreshAfterSave();
      }
    });
  };

  const removeRep = async (eventRegistrationId) => {
    if (!eventRegistrationId || eventRegistrationId === '1') {
      return;
    }
    const removed = await dispatch(
      removePendingSponsorRep(eventRegistrationId),
    );
    if (removed) {
      await refreshAfterSave();
    }
  };

  const proceedToPayment = () => {
    const optionId =
      sponsorshipData?.sponsorship_option?.id ||
      sponsorshipData?.pending_reps?.[0]?.event_option_id;
    dispatch(
      payForSponsorReps(
        eventId,
        optionId,
        navigate,
        Number(sponsorRegistrationId) || sponsorshipData?.sponsor?.id,
      ),
    );
  };

  return (
    <AddVendorRepsComponent
      sponsorshipData={sponsorshipData}
      eventLocalChapterData={eventLocalChapterData}
      allEventOptionsFoodData={allEventOptionsFoodData}
      saveRep={saveRep}
      updateRep={updateRep}
      removeRep={removeRep}
      proceedToPayment={proceedToPayment}
    />
  );
};

export default AddVendorRepsContainer;
